import React, { useState, useRef } from 'react';
import { parseCSV } from '../utils/csvParser';

function InputPanel({ onDataLoad, metrics = {}, onMetricsChange, onReset }) {
  const [csvInput, setCsvInput] = useState('');
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileInputRef = useRef(null);

  // Default metrics (matching FY26 requirements)
  const [startingArr, setStartingArr] = useState((metrics.startingArr ?? 10522000) / 1000000); // Convert to millions for display
  const [arpa, setArpa] = useState((metrics.arpa ?? 194000) / 1000); // Convert to thousands for display
  const [sqlToSqoRate, setSqlToSqoRate] = useState((metrics.sqlToSqoRate ?? 0.434) * 100); // Convert to percentage
  const [sqoToCwRate, setSqoToCwRate] = useState((metrics.sqoToCwRate ?? 0.288) * 100); // Convert to percentage
  const [pipelineCoverageRatio, setPipelineCoverageRatio] = useState(metrics.pipelineCoverageRatio ?? 3.5);
  const [startingSqls, setStartingSqls] = useState(metrics.startingSqls ?? 55);
  const [monthlyGrowthRate, setMonthlyGrowthRate] = useState((metrics.monthlyGrowthRate ?? 0.10) * 100); // Convert to percentage

  const handleLoadData = () => {
    try {
      setError('');
      console.log('Parsing CSV...', csvInput.substring(0, 100));
      const data = parseCSV(csvInput);
      console.log('Parsed data:', data);
      console.log('Calling onDataLoad...');
      onDataLoad(data);
      console.log('Data loaded successfully!');
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        setError('');
        const content = e.target?.result;
        setCsvInput(content);
        const data = parseCSV(content);
        onDataLoad(data);
      } catch (err) {
        setError(err.message);
        setFileName('');
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
      setFileName('');
    };

    reader.readAsText(file);
  };

  const handleClearData = () => {
    setCsvInput('');
    setFileName('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMetricsChange = () => {
    if (onMetricsChange) {
      onMetricsChange({
        startingArr: startingArr * 1000000,              // Convert M to actual value
        arpa: arpa * 1000,                                // Convert K to actual value
        sqlToSqoRate: sqlToSqoRate / 100,                 // Convert percentage to decimal
        sqoToCwRate: sqoToCwRate / 100,                   // Convert percentage to decimal
        pipelineCoverageRatio: pipelineCoverageRatio,
        startingSqls: startingSqls,
        monthlyGrowthRate: monthlyGrowthRate / 100,       // Convert percentage to decimal
        decemberSurge: 25,                                // Fixed value
        spreadMultiplier: metrics.spreadMultiplier ?? 1.0
      });
    }
  };

  const handleResetMetrics = () => {
    // Define reset values in display format (adjusted for Q1 $4.2M, Q2 $5.8M targets)
    const resetValues = {
      startingArr: 10.522,      // $10.522M (Dec 2025)
      arpa: 194,                // $194K
      sqlToSqoRate: 43.4,       // 43.4%
      sqoToCwRate: 28.8,        // 28.8%
      pipelineCoverageRatio: 3.5,
      startingSqls: 55,         // Jan 2026 SQLs (adjusted for Q1 $4.2M target)
      monthlyGrowthRate: 10,    // 10%
      spreadMultiplier: 1.0
    };

    // Update display state
    setStartingArr(resetValues.startingArr);
    setArpa(resetValues.arpa);
    setSqlToSqoRate(resetValues.sqlToSqoRate);
    setSqoToCwRate(resetValues.sqoToCwRate);
    setPipelineCoverageRatio(resetValues.pipelineCoverageRatio);
    setStartingSqls(resetValues.startingSqls);
    setMonthlyGrowthRate(resetValues.monthlyGrowthRate);

    // Trigger reset callback first to reset dashboard to static data
    if (onReset) {
      onReset();
    }

    // Trigger metrics change with explicit reset values (not relying on state)
    setTimeout(() => {
      if (onMetricsChange) {
        onMetricsChange({
          startingArr: resetValues.startingArr * 1000000,
          arpa: resetValues.arpa * 1000,
          sqlToSqoRate: resetValues.sqlToSqoRate / 100,
          sqoToCwRate: resetValues.sqoToCwRate / 100,
          pipelineCoverageRatio: resetValues.pipelineCoverageRatio,
          startingSqls: resetValues.startingSqls,
          monthlyGrowthRate: resetValues.monthlyGrowthRate / 100,
          decemberSurge: 25,
          spreadMultiplier: resetValues.spreadMultiplier
        });
      }
    }, 50);
  };

  return (
    <div className="w-full bg-white rounded-lg shadow-md p-6 h-fit">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Data Input</h2>
        <button
          onClick={handleResetMetrics}
          className="bg-green-600 text-white px-3 py-1 rounded font-medium hover:bg-green-700 transition-colors text-sm"
        >
          Reset
        </button>
      </div>

      {/* FY26 Forecast Metrics */}
      <div className="mb-6 p-4 bg-green-50 rounded border border-green-200">
        <h3 className="text-sm font-semibold text-green-900 mb-3">FY26 Forecast Metrics</h3>

        {/* Core Assumptions */}
        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
          <h4 className="text-xs font-semibold text-blue-900 mb-2">Core Assumptions</h4>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              Starting ARR (Dec 2025): <span className="text-blue-700 font-semibold">${startingArr.toFixed(3)}M</span> <span className="text-gray-500">(Default: $10.522M)</span>
            </label>
            <input
              type="range"
              min="5"
              max="20"
              step="0.5"
              value={startingArr}
              onChange={(e) => setStartingArr(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              ARPA (Average Deal Size): <span className="text-blue-700 font-semibold">${arpa}K</span> <span className="text-gray-500">(Default: $194K)</span>
            </label>
            <input
              type="range"
              min="100"
              max="400"
              step="5"
              value={arpa}
              onChange={(e) => setArpa(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              SQL → SQO Conversion: <span className="text-blue-700 font-semibold">{sqlToSqoRate.toFixed(1)}%</span> <span className="text-gray-500">(Default: 43.4%)</span>
            </label>
            <input
              type="range"
              min="25"
              max="65"
              step="0.5"
              value={sqlToSqoRate}
              onChange={(e) => setSqlToSqoRate(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              SQO → Closed Won Conversion: <span className="text-blue-700 font-semibold">{sqoToCwRate.toFixed(1)}%</span> <span className="text-gray-500">(Default: 28.8%)</span>
            </label>
            <input
              type="range"
              min="15"
              max="45"
              step="0.5"
              value={sqoToCwRate}
              onChange={(e) => setSqoToCwRate(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              Pipeline Coverage Ratio: <span className="text-blue-700 font-semibold">{pipelineCoverageRatio.toFixed(1)}x</span> <span className="text-gray-500">(Default: 3.5x)</span>
            </label>
            <input
              type="range"
              min="2"
              max="6"
              step="0.1"
              value={pipelineCoverageRatio}
              onChange={(e) => setPipelineCoverageRatio(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* Growth Parameters */}
        <div className="mb-4 p-3 bg-purple-50 rounded border border-purple-200">
          <h4 className="text-xs font-semibold text-purple-900 mb-2">Growth Parameters</h4>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              Starting SQLs (Jan 2026): <span className="text-purple-700 font-semibold">{startingSqls}</span> <span className="text-gray-500">(Default: 55 for Q1 $4.2M)</span>
            </label>
            <input
              type="range"
              min="20"
              max="150"
              step="1"
              value={startingSqls}
              onChange={(e) => setStartingSqls(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-1">
              Monthly Growth Rate: <span className="text-purple-700 font-semibold">{monthlyGrowthRate.toFixed(1)}%</span> <span className="text-gray-500">(Default: 10%)</span>
            </label>
            <input
              type="range"
              min="0"
              max="25"
              step="0.5"
              value={monthlyGrowthRate}
              onChange={(e) => setMonthlyGrowthRate(parseFloat(e.target.value))}
              onMouseUp={handleMetricsChange}
              onTouchEnd={handleMetricsChange}
              className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

        </div>

        {/* Advanced Button */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full bg-gray-600 text-white py-2 rounded font-medium hover:bg-gray-700 transition-colors text-sm"
        >
          {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
        </button>
      </div>

      {/* Advanced CSV Upload Section */}
      {showAdvanced && (
        <>
          {/* File Upload Option */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload CSV File
            </label>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-file-input"
              />
              <label
                htmlFor="csv-file-input"
                className="block w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-center text-sm text-gray-600"
              >
                {fileName ? (
                  <span className="text-green-600 font-medium">✓ {fileName}</span>
                ) : (
                  <>
                    <span className="text-blue-600">Choose file</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-500 font-medium">OR</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Paste Option */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste CSV Data
            </label>
            <textarea
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
              placeholder="Paste your CSV data here..."
              className="w-full h-32 p-3 border border-gray-300 rounded text-sm font-mono resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={handleLoadData}
              className="flex-1 bg-blue-600 text-white py-2 rounded font-medium hover:bg-blue-700 transition-colors"
            >
              Load Data
            </button>
            {(csvInput || fileName) && (
              <button
                onClick={handleClearData}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-medium hover:bg-gray-400 transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-4 p-3 bg-blue-50 rounded text-xs text-blue-800">
            <div className="font-semibold mb-1">Supported Formats:</div>
            <ul className="space-y-1">
              <li>• Native GTM format (months as columns)</li>
              <li>• Standard format (months as rows)</li>
            </ul>
          </div>
        </>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
          <div className="font-semibold mb-1">Error:</div>
          <div>{error}</div>
        </div>
      )}
    </div>
  );
}

export default InputPanel;
